/**
 * Reusable Button Component Function with Tailwind Classes
 *
 * @param {Object} props - Button properties
 * @param {string} [props.type='button'] - Button behavior type ('button', 'submit', 'reset')
 * @param {string} [props.title='Click me'] - Inner text or HTML of the button
 * @param {string} [props.icon] - Icon to be displayed (optional)
 * @param {string} [props.class=''] - Additional CSS classes
 * @param {string} [props.style] - Inline style string (optional)
 * @param {string} [props.id] - HTML id
 * @param {boolean} [props.disabled=false] - Boolean to disable the button
 * @param {string} [props.aria_label] - Aria label for accessibility
 * @param {boolean|string} [props.aria_hidden] - Aria hidden attribute
 * @param {Object} [props.data_attributes={}] - Hash of key-value pairs for data-* attributes
 * @param {Object} [props.attributes={}] - Additional HTML attributes (e.g. tabindex, role)
 * @param {string} [props.btn_type='contained'] - Visual type ('text', 'outlined', 'contained')
 * @param {string} [props.variant='primary'] - Color/style variant ('primary', 'secondary', 'tertiary')
 * @param {boolean} [props.isAnchor=false] - Boolean to determine if the button should be an anchor tag
 * @param {string} [props.href] - URL for anchor tag (required when isAnchor is true)
 * @returns {string} HTML string for the button component
 */
function createButton(props = {}) {
  // Set defaults
  const {
    type = "button",
    title = "Click me",
    icon,
    class: additionalClasses = "",
    style,
    id,
    disabled = false,
    aria_label,
    aria_hidden,
    data_attributes = {},
    attributes = {},
    btn_type = "contained",
    variant = "primary",
    isAnchor = false,
    href,
  } = props;

  // Build base classes
  const baseClasses =
    `btn btn--${btn_type} btn--${variant} ${additionalClasses}`.trim();

  // Build data attributes string
  const dataAttrsString = Object.entries(data_attributes)
    .map(([key, value]) => `data-${key}="${value}"`)
    .join(" ");

  // Build additional attributes string
  const additionalAttrsString = Object.entries(attributes)
    .map(([key, value]) => `${key}="${value}"`)
    .join(" ");

  // Build common attributes
  const commonAttrs = [
    `class="${baseClasses}"`,
    style ? `style="${style}"` : "",
    id ? `id="${id}"` : "",
    aria_label ? `aria-label="${aria_label}"` : "",
    aria_hidden !== undefined ? `aria-hidden="${aria_hidden}"` : "",
    dataAttrsString,
    additionalAttrsString,
  ]
    .filter(Boolean)
    .join(" ");

  // Build inner content
  const innerContent = `${title}${icon ? `<span class="icon">${icon}</span>` : ""
    }`;

  // Return anchor or button based on isAnchor flag
  if (isAnchor) {
    const hrefAttr = href ? `href="${href}"` : "";
    const disabledAttr = disabled ? "disabled" : "";

    return `<a ${hrefAttr} ${commonAttrs} ${disabledAttr}>${innerContent}</a>`;
  } else {
    const typeAttr = `type="${type}"`;
    const disabledAttr = disabled ? "disabled" : "";

    return `<button ${typeAttr} ${commonAttrs} ${disabledAttr}>${innerContent}</button>`;
  }
}

/**
 * Legacy method - kept for backward compatibility
 * Creates a button element directly (DOM element, not HTML string)
 */
CarouselManager.prototype.createButton = function (
  buttonData,
  additionalClass
) {
  var button = document.createElement("a");
  button.href = buttonData.link || "#";
  button.textContent = buttonData.label;

  // Build button classes using your button snippet pattern
  var btnType = buttonData.type || "contained";
  var btnVariant = buttonData.variant || "primary";

  // Base classes matching your button snippet
  var classes = [
    "btn",
    "btn--" + btnType,
    "btn--" + btnVariant,
    "text-btn",
    "px-5",
    "py-3",
    "max-w-[14.5rem]",
    "w-full",
    "inline-block",
    "text-center",
  ];

  if (additionalClass) {
    classes.push(additionalClass);
  }

  button.className = classes.join(" ");

  return button;
};

// Alternative method if you need more specific styling control
CarouselManager.prototype.getButtonClasses = function (
  buttonType,
  buttonVariant
) {
  var baseClasses =
    "btn text-btn px-5 py-3 max-w-[14.5rem] w-full inline-block text-center";
  var typeClass = "btn--" + (buttonType || "contained");
  var variantClass = "btn--" + (buttonVariant || "primary");

  return baseClasses + " " + typeClass + " " + variantClass;
};

function CarouselManager(options) {
  this.landscapeImageElement = options.landscapeImageElement;
  this.portraitImageElement = options.portraitImageElement;
  this.landscapeVideoElement = options.landscapeVideoElement;
  this.portraitVideoElement = options.portraitVideoElement;
  // Keep backward compatibility with single video element
  this.videoElement = options.videoElement || options.landscapeVideoElement;
  this.slideNumberElement = options.slideNumberElement;
  this.pausePlayButton = options.pausePlayButton;
  this.muteUnmuteButton = options.muteUnmuteButton;
  this.nextSlideButton = options.nextSlideButton;
  this.prevSlideButton = options.prevSlideButton;
  this.timerProgressElement = options.timerProgressElement;
  this.titleElement = options.titleElement;
  this.subtitleElement = options.subtitleElement;
  this.buttonsContainer = options.buttonsContainer;
  this.overlayElement = options.overlayElement;
  this.carouselContentElement = options.carouselContentElement;
  this.slideContentElement = options.slideContentElement;
  this.slides = options.slides;
  this.totalSlides = this.slides.length;
  this.currentSlide = 0;
  this.isPaused = false;
  this.intervalDuration = options.intervalDuration || 5000;
  this.startTime = null;
  this.elapsedTime = 0;
  this.timerAnimationFrame = null;

  this.init();
}

CarouselManager.prototype.init = function () {
  if (this.totalSlides < 2) {
    this.showSlideImmediate(this.currentSlide);
    return;
  }

  this.showSlideImmediate(this.currentSlide);
  this.startAutoPlay();
  this.attachEventListeners();
};

CarouselManager.prototype.attachEventListeners = function () {
  this.nextSlideButton.addEventListener("click", () => this.manualNextSlide());
  this.prevSlideButton.addEventListener("click", () => this.manualPrevSlide());
  this.pausePlayButton.addEventListener("click", () => this.togglePausePlay());
  this.muteUnmuteButton.addEventListener("click", () =>
    this.toggleVideoSound()
  );

  // Add event listeners for both video elements if they exist
  if (this.landscapeVideoElement) {
    this.landscapeVideoElement.addEventListener("ended", () => {
      this.manualNextSlide();
      this.togglePausePlay();
    });
  }
  
  if (this.portraitVideoElement) {
    this.portraitVideoElement.addEventListener("ended", () => {
      this.manualNextSlide();
      this.togglePausePlay();
    });
  }
  
  // Backward compatibility: single video element
  if (this.videoElement && this.videoElement !== this.landscapeVideoElement) {
    this.videoElement.addEventListener("ended", () => {
      this.manualNextSlide();
      this.togglePausePlay();
    });
  }
};

CarouselManager.prototype.updateSlideNumber = function () {
  this.slideNumberElement.textContent = `${this.currentSlide + 1}/${this.totalSlides
    }`;
};

CarouselManager.prototype.getDesktopContentPositionClasses = function (
  desktopContentPosition
) {
  var desktopContentPositionClasses = "";
  var textAlignClass = "text-left";

  switch (desktopContentPosition) {
    case "top-left":
      desktopContentPositionClasses = "justify-start items-start";
      textAlignClass = "text-left";
      break;
    case "top-center":
      desktopContentPositionClasses = "justify-start items-center";
      textAlignClass = "text-center";
      break;
    case "top-right":
      desktopContentPositionClasses = "justify-start items-end";
      textAlignClass = "text-right";
      break;
    case "center-left":
      desktopContentPositionClasses = "justify-center items-start";
      textAlignClass = "text-left";
      break;
    case "center-center":
      desktopContentPositionClasses = "justify-center items-center";
      textAlignClass = "text-center";
      break;
    case "center-right":
      desktopContentPositionClasses = "justify-center items-end";
      textAlignClass = "text-right";
      break;
    case "bottom-left":
      desktopContentPositionClasses = "justify-end items-start";
      textAlignClass = "text-left";
      break;
    case "bottom-center":
      desktopContentPositionClasses = "justify-end items-center";
      textAlignClass = "text-center";
      break;
    case "bottom-right":
      desktopContentPositionClasses = "justify-end items-end";
      textAlignClass = "text-right";
      break;
    default:
      desktopContentPositionClasses = "justify-center items-start";
      textAlignClass = "text-left";
  }

  return {
    positionClasses: desktopContentPositionClasses,
    textAlignClass: textAlignClass,
  };
};

CarouselManager.prototype.getMaxWidthClass = function (contentMaxWidth) {
  var maxWidthClass = "";

  switch (contentMaxWidth) {
    case "sm":
      maxWidthClass = "max-w-sm";
      break;
    case "md":
      maxWidthClass = "max-w-md";
      break;
    case "lg":
      maxWidthClass = "max-w-lg";
      break;
    case "xl":
      maxWidthClass = "max-w-xl";
      break;
    case "2xl":
      maxWidthClass = "max-w-2xl";
      break;
    case "3xl":
      maxWidthClass = "max-w-3xl";
      break;
    case "4xl":
      maxWidthClass = "max-w-4xl";
      break;
    case "5xl":
      maxWidthClass = "max-w-5xl";
      break;
    case "full":
      maxWidthClass = "max-w-full";
      break;
    case "none":
      maxWidthClass = "";
      break;
    default:
      maxWidthClass = "max-w-2xl";
  }

  return maxWidthClass;
};

CarouselManager.prototype.updateSlideContent = function (slide) {
  this.titleElement.innerHTML = slide.title || "";
  this.subtitleElement.innerHTML = slide.subtitle || "";
  const isMobile = window.innerWidth < window.innerHeight;
  const contentPosition = isMobile
    ? slide.mobileContentPosition
    : slide.desktopContentPosition;

  var positionData = this.getDesktopContentPositionClasses(
    contentPosition || "center-left"
  );
  var maxWidthClass = this.getMaxWidthClass(slide.contentMaxWidth || "2xl");

  this.carouselContentElement.className =
    "boxed-section 3xl:!px-6xl !max-w-[100dvw] relative z-10 flex flex-col h-full " +
    positionData.positionClasses;

  this.slideContentElement.className =
    "w-full " + maxWidthClass + " " + positionData.textAlignClass;

  var buttonsJustifyClass = "";
  if (positionData.textAlignClass === "text-center") {
    buttonsJustifyClass = "justify-center";
  } else if (positionData.textAlignClass === "text-right") {
    buttonsJustifyClass = "justify-end";
  }
  this.buttonsContainer.className =
    "flex gap-4 my-10 mb-[4.25rem] transition-opacity duration-300 ease-in-out " +
    buttonsJustifyClass;

  this.updateButtons(slide);
};

CarouselManager.prototype.updateButtons = function (slide) {
  this.buttonsContainer.innerHTML = "";

  if (slide.button1 && slide.button1.enabled && slide.button1.label) {
    const button1Html = this.createButtonWithFunction(
      slide.button1,
      "carousel-btn-1"
    );
    this.buttonsContainer.insertAdjacentHTML("beforeend", button1Html);
  }

  if (slide.button2 && slide.button2.enabled && slide.button2.label) {
    const button2Html = this.createButtonWithFunction(
      slide.button2,
      "carousel-btn-2"
    );
    this.buttonsContainer.insertAdjacentHTML("beforeend", button2Html);
  }
};

CarouselManager.prototype.createButtonWithFunction = function (
  buttonData,
  additionalClass
) {
  const buttonProps = {
    title: buttonData.label,
    href: buttonData.link || "#",
    isAnchor: buttonData.isAnchor !== undefined ? buttonData.isAnchor : true,
    type: buttonData.behavior_type || "default",
    btn_type: buttonData.type || "contained",
    variant: buttonData.variant || "primary",
    class: `text-btn px-5 py-3 max-w-[14.5rem] w-full ${additionalClass || ""}`,
    disabled: buttonData.disabled || false,
    id: buttonData.id || null,
    aria_label: buttonData.aria_label || null,
    data_attributes: buttonData.data_attributes || {},
    attributes: buttonData.attributes || {},
    icon: buttonData.icon,
    style: buttonData.style,
  };

  return createButton(buttonProps);
};

CarouselManager.prototype.syncVideoProgress = function () {
  const isMobile = window.innerWidth < window.innerHeight;
  const activeVideo = isMobile ? this.portraitVideoElement : this.landscapeVideoElement;
  
  // Fallback to single video element for backward compatibility
  const videoEl = activeVideo || this.videoElement;
  
  if (!videoEl || !videoEl.duration) return;
  
  const progress = (videoEl.currentTime / videoEl.duration) * 100;
  this.timerProgressElement.style.width = `${progress}%`;
  
  if (videoEl.currentTime < videoEl.duration) {
    this.videoProgressFrame = requestAnimationFrame(this.syncVideoProgress.bind(this));
  }
};

CarouselManager.prototype.showSlideImmediate = function (index) {
  const slide = this.slides[index];
  const isMobile = window.innerWidth < window.innerHeight;
  
  // Stop and reset any playing videos
  this.stopAllVideos();
  
  // Check for video based on orientation
  const hasVideo = isMobile 
    ? (slide.portraitVideo || slide.video) 
    : (slide.landscapeVideo || slide.video);

  if (hasVideo) {
    this.muteUnmuteButton.classList.remove("hidden");
    this.muteUnmuteButton.classList.add("flex");
    this.pausePlayButton.classList.remove("flex");
    this.pausePlayButton.classList.add("hidden");

    // Set video source based on orientation
    if (this.landscapeVideoElement && this.portraitVideoElement) {
      const landscapeSrc = slide.landscapeVideo || slide.video;
      const portraitSrc = slide.portraitVideo || slide.video;
      
      if (landscapeSrc) {
        this.landscapeVideoElement.src = landscapeSrc;
        this.landscapeVideoElement.style.display = isMobile ? "none" : "block";
      }
      
      if (portraitSrc) {
        this.portraitVideoElement.src = portraitSrc;
        this.portraitVideoElement.style.display = isMobile ? "block" : "none";
      }
      
      // Reset progress bar
      this.timerProgressElement.style.transition = "none";
      this.timerProgressElement.style.width = "0%";
      
      const activeVideo = isMobile ? this.portraitVideoElement : this.landscapeVideoElement;
      activeVideo.play().then(() => {
        this.syncVideoProgress();
      });
    } else if (this.videoElement) {
      // Backward compatibility: single video element
      this.videoElement.src = hasVideo;
      this.videoElement.style.display = "block";
      
      this.timerProgressElement.style.transition = "none";
      this.timerProgressElement.style.width = "0%";
      
      this.videoElement.play().then(() => {
        this.syncVideoProgress();
      });
    }
    
    this.landscapeImageElement.style.display = "none";
    this.portraitImageElement.style.display = "none";
    this.landscapeImageElement.removeAttribute("src");
    this.portraitImageElement.removeAttribute("src");
  } else {
    this.muteUnmuteButton.classList.remove("flex");
    this.muteUnmuteButton.classList.add("hidden");
    this.pausePlayButton.classList.remove("hidden");
    this.pausePlayButton.classList.add("flex");

    if (slide.landscapeImage && slide.landscapeImage !== "") {
      this.landscapeImageElement.src = slide.landscapeImage;
      this.landscapeImageElement.style.display = "block";
    } else {
      this.landscapeImageElement.style.display = "none";
      this.landscapeImageElement.removeAttribute("src");
    }
    
    if (slide.portraitImage && slide.portraitImage !== "") {
      this.portraitImageElement.src = slide.portraitImage;
      this.portraitImageElement.style.display = "block";
    } else {
      this.portraitImageElement.style.display = "none";
      this.portraitImageElement.removeAttribute("src");
    }
    
    // Hide all video elements
    if (this.landscapeVideoElement) {
      this.landscapeVideoElement.style.display = "none";
      this.landscapeVideoElement.removeAttribute("src");
    }
    if (this.portraitVideoElement) {
      this.portraitVideoElement.style.display = "none";
      this.portraitVideoElement.removeAttribute("src");
    }
    if (this.videoElement) {
      this.videoElement.style.display = "none";
      this.videoElement.removeAttribute("src");
    }
  }
  
  this.updateSlideContent(slide);
  this.updateSlideNumber();
};

CarouselManager.prototype.showSlide = function (index) {
  const self = this;
  const slide = this.slides[index];
  const isMobile = window.innerWidth < window.innerHeight;

  // Stop and reset any playing videos
  this.stopAllVideos();

  if (slide.showOverlay) {
    const opacity = slide.overlayOpacity / 100;
    this.overlayElement.style.backgroundColor = `rgba(0, 0, 0, ${opacity})`;
    this.overlayElement.style.opacity = opacity;
  } else {
    this.overlayElement.style.backgroundColor = "transparent";
    this.overlayElement.style.opacity = 0;
  }

  this.titleElement.style.transition = "opacity 300ms ease-in-out";
  this.subtitleElement.style.transition = "opacity 300ms ease-in-out";
  this.buttonsContainer.style.transition = "opacity 300ms ease-in-out";

  this.titleElement.style.opacity = "0";
  this.subtitleElement.style.opacity = "0";
  this.buttonsContainer.style.opacity = "0";

  setTimeout(() => {
    // Check for video based on orientation
    const hasVideo = isMobile 
      ? (slide.portraitVideo || slide.video) 
      : (slide.landscapeVideo || slide.video);

    if (hasVideo) {
      self.pauseAutoPlay();
      self.muteUnmuteButton.classList.remove("hidden");
      self.muteUnmuteButton.classList.add("flex");
      self.pausePlayButton.classList.remove("flex");
      self.pausePlayButton.classList.add("hidden");

      // Set video source based on orientation
      if (self.landscapeVideoElement && self.portraitVideoElement) {
        const landscapeSrc = slide.landscapeVideo || slide.video;
        const portraitSrc = slide.portraitVideo || slide.video;
        
        if (landscapeSrc) {
          self.landscapeVideoElement.src = landscapeSrc;
          self.landscapeVideoElement.style.display = isMobile ? "none" : "block";
        }
        
        if (portraitSrc) {
          self.portraitVideoElement.src = portraitSrc;
          self.portraitVideoElement.style.display = isMobile ? "block" : "none";
        }
        
        // Reset progress bar
        self.timerProgressElement.style.transition = "none";
        self.timerProgressElement.style.width = "0%";
        
        const activeVideo = isMobile ? self.portraitVideoElement : self.landscapeVideoElement;
        activeVideo.play().then(() => {
          self.syncVideoProgress();
        });
      } else if (self.videoElement) {
        // Backward compatibility: single video element
        self.videoElement.src = hasVideo;
        self.videoElement.style.display = "block";
        
        self.timerProgressElement.style.transition = "none";
        self.timerProgressElement.style.width = "0%";
        
        self.videoElement.play().then(() => {
          self.syncVideoProgress();
        });
      }
      
      self.landscapeImageElement.style.display = "none";
      self.portraitImageElement.style.display = "none";
      self.landscapeImageElement.removeAttribute("src");
      self.portraitImageElement.removeAttribute("src");
    } else {
      self.muteUnmuteButton.classList.remove("flex");
      self.muteUnmuteButton.classList.add("hidden");
      self.pausePlayButton.classList.remove("hidden");
      self.pausePlayButton.classList.add("flex");

      if (slide.landscapeImage && slide.landscapeImage !== "") {
        self.landscapeImageElement.src = slide.landscapeImage;
        self.landscapeImageElement.style.display = "block";
      } else {
        self.landscapeImageElement.style.display = "none";
        self.landscapeImageElement.removeAttribute("src");
      }
      
      if (slide.portraitImage && slide.portraitImage !== "") {
        self.portraitImageElement.src = slide.portraitImage;
        self.portraitImageElement.style.display = "block";
      } else {
        self.portraitImageElement.style.display = "none";
        self.portraitImageElement.removeAttribute("src");
      }
      
      // Hide all video elements
      if (self.landscapeVideoElement) {
        self.landscapeVideoElement.style.display = "none";
        self.landscapeVideoElement.removeAttribute("src");
      }
      if (self.portraitVideoElement) {
        self.portraitVideoElement.style.display = "none";
        self.portraitVideoElement.removeAttribute("src");
      }
      if (self.videoElement) {
        self.videoElement.style.display = "none";
        self.videoElement.removeAttribute("src");
      }
    }

    self.updateSlideContent(slide);

    setTimeout(() => {
      self.titleElement.style.opacity = "1";
      self.subtitleElement.style.opacity = "1";
      self.buttonsContainer.style.opacity = "1";
    }, 100);
    self.updateSlideNumber();
  }, 300);
};

CarouselManager.prototype.stopAllVideos = function () {
  // Cancel any ongoing video progress tracking
  if (this.videoProgressFrame) {
    cancelAnimationFrame(this.videoProgressFrame);
    this.videoProgressFrame = null;
  }
  
  // Stop and reset landscape video
  if (this.landscapeVideoElement) {
    this.landscapeVideoElement.pause();
    this.landscapeVideoElement.currentTime = 0;
  }
  
  // Stop and reset portrait video
  if (this.portraitVideoElement) {
    this.portraitVideoElement.pause();
    this.portraitVideoElement.currentTime = 0;
  }
  
  // Stop and reset single video element (backward compatibility)
  if (this.videoElement) {
    this.videoElement.pause();
    this.videoElement.currentTime = 0;
  }
};

CarouselManager.prototype.nextSlide = function () {
  this.currentSlide = (this.currentSlide + 1) % this.totalSlides;
  this.showSlide(this.currentSlide);
};

CarouselManager.prototype.prevSlide = function () {
  this.currentSlide =
    (this.currentSlide - 1 + this.totalSlides) % this.totalSlides;
  this.showSlide(this.currentSlide);
};

CarouselManager.prototype.manualNextSlide = function () {
  this.nextSlide();
  if (!this.isPaused) {
    this.resetTimer();
  } else {
    this.elapsedTime = 0;
    this.timerProgressElement.style.transition = "none";
    this.timerProgressElement.style.width = "0%";
  }
};

CarouselManager.prototype.manualPrevSlide = function () {
  this.prevSlide();
  if (!this.isPaused) {
    this.resetTimer();
  } else {
    this.elapsedTime = 0;
    this.timerProgressElement.style.transition = "none";
    this.timerProgressElement.style.width = "0%";
  }
};

CarouselManager.prototype.resetTimer = function () {
  cancelAnimationFrame(this.timerAnimationFrame);
  this.startTime = performance.now();
  this.elapsedTime = 0;
  this.timerProgressElement.style.transition = "none";
  this.timerProgressElement.style.width = "0%";
  this.timerAnimationFrame = requestAnimationFrame(
    this.animateTimer.bind(this)
  );
};

CarouselManager.prototype.animateTimer = function (timestamp) {
  if (this.isPaused) return;

  if (!this.startTime) this.startTime = timestamp;
  this.elapsedTime = timestamp - this.startTime;

  const progress = Math.min(this.elapsedTime / this.intervalDuration, 1);
  this.timerProgressElement.style.width = `${progress * 100}%`;

  if (progress < 1) {
    this.timerAnimationFrame = requestAnimationFrame(
      this.animateTimer.bind(this)
    );
  } else {
    this.nextSlide();
    this.resetTimer();
  }
};

CarouselManager.prototype.startAutoPlay = function () {
  this.resetTimer();
};

CarouselManager.prototype.stopAutoPlay = function () {
  cancelAnimationFrame(this.timerAnimationFrame);
  this.timerAnimationFrame = null;
  
  if (this.videoProgressFrame) {
    cancelAnimationFrame(this.videoProgressFrame);
    this.videoProgressFrame = null;
  }
  
  this.isPaused = true;
};

CarouselManager.prototype.pauseAutoPlay = function () {
  this.isPaused = true;
  cancelAnimationFrame(this.timerAnimationFrame);

  this.elapsedTime = performance.now() - this.startTime;
  const progress = Math.min(this.elapsedTime / this.intervalDuration, 1) * 100;
  this.timerProgressElement.style.transition = "none";
  this.timerProgressElement.style.width = `${progress}%`;
  this.timerProgressElement.offsetHeight;

  this.pausePlayButton.innerHTML =
    '<svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20"><path d="M6 4l8 6-8 6V4z" /></svg>';
};

CarouselManager.prototype.resumeAutoPlay = function () {
  this.isPaused = false;
  this.startTime = performance.now() - this.elapsedTime;
  this.timerAnimationFrame = requestAnimationFrame(
    this.animateTimer.bind(this)
  );

  this.pausePlayButton.innerHTML =
    '<svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20"><path d="M6 4h2v12H6V4zm6 0h2v12h-2V4z" /></svg>';
};

CarouselManager.prototype.togglePausePlay = function () {
  if (this.isPaused) {
    this.resumeAutoPlay();
  } else {
    this.pauseAutoPlay();
  }
};

CarouselManager.prototype.muteVideo = function () {
  // Mute all video elements
  if (this.landscapeVideoElement) this.landscapeVideoElement.muted = true;
  if (this.portraitVideoElement) this.portraitVideoElement.muted = true;
  if (this.videoElement) this.videoElement.muted = true;
  
  this.muteUnmuteButton.innerHTML =
    '<svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M12 3.75v16.5a.75.75 0 0 1-1.255.555L5.46 16H2.75A1.75 1.75 0 0 1 1 14.25v-4.5C1 8.784 1.784 8 2.75 8h2.71l5.285-4.805A.75.75 0 0 1 12 3.75M6.255 9.305a.75.75 0 0 1-.505.195h-3a.25.25 0 0 0-.25.25v4.5c0 .138.112.25.25.25h3c.187 0 .367.069.505.195l4.245 3.86V5.445ZM16.28 8.22a.75.75 0 1 0-1.06 1.06L17.94 12l-2.72 2.72a.75.75 0 1 0 1.06 1.06L19 13.06l2.72 2.72a.75.75 0 1 0 1.06-1.06L20.06 12l2.72-2.72a.75.75 0 0 0-1.06-1.06L19 10.94z"/></svg>';
};

CarouselManager.prototype.unmuteVideo = function () {
  // Unmute all video elements
  if (this.landscapeVideoElement) this.landscapeVideoElement.muted = false;
  if (this.portraitVideoElement) this.portraitVideoElement.muted = false;
  if (this.videoElement) this.videoElement.muted = false;
  
  this.muteUnmuteButton.innerHTML =
    '<svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path fill-rule="evenodd" d="M11.553 3.064A.75.75 0 0 1 12 3.75v16.5a.75.75 0 0 1-1.255.555L5.46 16H2.75A1.75 1.75 0 0 1 1 14.25v-4.5C1 8.784 1.784 8 2.75 8h2.71l5.285-4.805a.75.75 0 0 1 .808-.13zM10.5 5.445l-4.245 3.86a.75.75 0 0 1-.505.195h-3a.25.25 0 0 0-.25.25v4.5c0 .138.112.25.25.25h3a.75.75 0 0 1 .505.195l4.245 3.86z"/><path d="M18.718 4.222a.75.75 0 0 1 1.06 0c4.296 4.296 4.296 11.26 0 15.556a.75.75 0 0 1-1.06-1.06 9.5 9.5 0 0 0 0-13.436.75.75 0 0 1 0-1.06"/><path d="M16.243 7.757a.75.75 0 1 0-1.061 1.061 4.5 4.5 0 0 1 0 6.364.75.75 0 0 0 1.06 1.06 6 6 0 0 0 0-8.485z"/></svg>';
};

CarouselManager.prototype.toggleVideoSound = function () {
  const isMobile = window.innerWidth < window.innerHeight;
  const activeVideo = isMobile ? this.portraitVideoElement : this.landscapeVideoElement;
  const videoEl = activeVideo || this.videoElement;
  
  if (videoEl && videoEl.muted) {
    this.unmuteVideo();
  } else {
    this.muteVideo();
  }
};

// Export for use in other modules
if (typeof module !== "undefined" && module.exports) {
  module.exports = { CarouselManager, createButton };
}